import { Component } from '@angular/core';
import { ClienteUnicoDto, ClienteUnicoServiceProxy, DomicilioClienteDto } from '../services/service-proxy/service-proxies';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AppService } from '../services/app.service';
import { MatSnackBar } from '@angular/material/snack-bar';

/** Domicilio con su indice original: domicilioPrincipal es un indice del array. */
interface DomicilioVista {
  dom : DomicilioClienteDto;
  indice : number;
  esPrincipal : boolean;
}

@Component({
  selector: 'app-customer-adress-list',
  templateUrl: './customer-adress-list.component.html',
  styleUrls: ['./customer-adress-list.component.scss']
})
export class CustomerAdressListComponent {

  isLoading = false;
  idUnico;
  model? : ClienteUnicoDto;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private snackBar: MatSnackBar,
              private clienteUnicoService: ClienteUnicoServiceProxy,
              private appService : AppService) {

                this.idUnico = appService.idUnico;
  }

  ngOnInit() {
    this.loadData();
  }

  loadData() {

    this.isLoading = true;
    this.clienteUnicoService
        .get(this.idUnico, null, false)
        .pipe(finalize( () => this.isLoading = false))
        .subscribe( (response) => this.model = response );
  }

  /**
   * El principal primero, como en el prototipo. Se arma una lista de vista y no
   * se reordena el array real, porque domicilioPrincipal es un indice y
   * moverlos lo dejaria apuntando a otro domicilio.
   */
  get domicilios() : DomicilioVista[] {

    const lista = (this.model?.domicilios ?? []).map((dom, indice) => ({
      dom,
      indice,
      esPrincipal : indice === this.model?.domicilioPrincipal
    }));

    return [
      ...lista.filter(x => x.esPrincipal),
      ...lista.filter(x => !x.esPrincipal)
    ];
  }

  get hayDomicilios() : boolean {
    return this.domicilios.length > 0;
  }

  /** "Av. Maipú 2340, piso 7 B" */
  calle(dom : DomicilioClienteDto) : string {

    const base = [dom.calle, dom.numero].filter(x => x).join(' ');

    const detalle = [
      dom.piso ? `piso ${dom.piso}` : null,
      dom.departamento || null
    ].filter(x => x).join(' ');

    return detalle ? `${base}, ${detalle}` : base;
  }

  /** "Vicente López, Buenos Aires · 1602" */
  ubicacion(dom : DomicilioClienteDto) : string {

    const lugar = [dom.localidad, dom.provincia].filter(x => x).join(', ');

    return dom.codigoPostal ? `${lugar} · ${dom.codigoPostal}` : lugar;
  }

  goToEdit(indice : number) {
    this.router.navigateByUrl(`/edit-customer-adress/${indice}`);
  }

  agregar() {
    this.router.navigateByUrl(`/add-customer-adress/${this.model?.idUnico}`);
  }

  markAsPrincipal(indice : number){
    if(!this.model) return;

    this.model.domicilioPrincipal = indice;
    this.save();
  }

  /**
   * Borrar reacomoda domicilioPrincipal, que es un indice del array.
   * Antes no se tocaba: borrar un domicilio anterior al principal dejaba el
   * principal apuntando a otro, y borrar el principal lo dejaba apuntando a su
   * vecino o fuera de rango.
   */
  delete(indice : number){

    if(!this.model?.domicilios) return;

    const borrado = this.model.domicilios[indice];
    const principalAnterior = this.model.domicilioPrincipal;

    this.model.domicilios.splice(indice, 1);
    this.model.domicilioPrincipal = this.recalcularPrincipal(principalAnterior, indice);

    this.save(() => this.ofrecerDeshacer(borrado, indice, principalAnterior));
  }

  private recalcularPrincipal(principal : number | undefined, borrado : number) : number | undefined {

    if(principal === undefined || principal === null) return principal;

    // Se borro el principal: no queda ninguno marcado.
    if(principal === borrado) return undefined;

    // Se borro uno antes: todos los siguientes corren un lugar.
    if(principal > borrado) return principal - 1;

    return principal;
  }

  private ofrecerDeshacer(borrado : DomicilioClienteDto, indice : number, principalAnterior : number | undefined) {

    const ref = this.snackBar.open('Domicilio eliminado.', 'Deshacer', { duration: 6000 });

    ref.onAction().subscribe(() => {

      if(!this.model?.domicilios) return;

      this.model.domicilios.splice(indice, 0, borrado);
      this.model.domicilioPrincipal = principalAnterior;
      this.save();
    });
  }

  save(alTerminar? : () => void) {

    if(this.model)
      this.model.mascotas = undefined;

    this.isLoading = true;
    this.clienteUnicoService
        .update(this.model)
        .pipe(finalize(() => this.isLoading = false))
        .subscribe(() => {
          this.loadData();
          if(alTerminar) alTerminar();
        });
  }
}
